/**
 * Session Engine v3 — EXPLORATORY / non-Canon prototype.
 *
 * The conditions-met "job to be done" catalog (see SPEC-03). Each job maps a
 * scenario the user starts from the dashboard to: which steps are required,
 * how each step defaults (declared vs collaborative), which tools are
 * recommended for collaborative steps, and the default container mode.
 */

import type { ContainerMode, StepId, StepMode } from './types';

export interface JobStepSpec {
  step: StepId;
  required: boolean;
  defaultMode: StepMode;
  recommendedToolIds?: string[];
}

export interface SessionJob {
  id: string;
  title: string;
  description: string;
  containerDefault: ContainerMode;
  requiredInformation: string[];
  steps: JobStepSpec[];
}

export const SESSION_JOBS: SessionJob[] = [
  {
    id: 'team_meeting',
    title: 'Team meeting prioritization',
    description: 'Everyone is in the room now. Narrow a list and agree what matters in one sitting.',
    containerDefault: 'single',
    requiredInformation: ['Who is in the room', 'A starting list of items'],
    steps: [
      { step: 'S1', required: true, defaultMode: 'declared' },
      { step: 'S2', required: true, defaultMode: 'collaborative', recommendedToolIds: ['dot_voting', 'stack_ranking'] },
      { step: 'S3', required: true, defaultMode: 'declared' },
      { step: 'S4', required: false, defaultMode: 'declared' },
      { step: 'S5', required: true, defaultMode: 'collaborative', recommendedToolIds: ['stack_ranking', 'simple_weighted_scoring'] },
    ],
  },
  {
    id: 'narrow_backlog',
    title: 'Narrow a big backlog to a shortlist',
    description: 'Too many items to rank. Converge to a manageable set (ideally under 25) before doing anything else.',
    containerDefault: 'multi',
    requiredInformation: ['A large item set', 'Rough selection criteria'],
    steps: [
      { step: 'S1', required: false, defaultMode: 'declared' },
      { step: 'S2', required: true, defaultMode: 'collaborative', recommendedToolIds: ['dot_voting', 'moscow', 'impact_effort_matrix', 'pareto_analysis'] },
      { step: 'S3', required: false, defaultMode: 'declared' },
      { step: 'S4', required: false, defaultMode: 'declared' },
      { step: 'S5', required: false, defaultMode: 'collaborative', recommendedToolIds: ['stack_ranking'] },
    ],
  },
  {
    id: 'score_shortlist',
    title: 'Score a defined shortlist on agreed criteria',
    description: 'The shortlist and criteria exist. Establish values and let the ranking flow from them.',
    containerDefault: 'single',
    requiredInformation: ['A defined shortlist', 'Agreed criteria and weights'],
    steps: [
      { step: 'S1', required: true, defaultMode: 'declared' },
      { step: 'S2', required: true, defaultMode: 'declared' },
      { step: 'S3', required: true, defaultMode: 'declared' },
      { step: 'S4', required: true, defaultMode: 'collaborative', recommendedToolIds: ['simple_weighted_scoring', 'rice', 'wsjf'] },
      { step: 'S5', required: true, defaultMode: 'collaborative', recommendedToolIds: ['simple_weighted_scoring', 'rice'] },
    ],
  },
  {
    id: 'pick_one_option',
    title: 'Pick one option among competing alternatives',
    description: 'Several options compete for the same slot. Compare them and choose one.',
    containerDefault: 'single',
    requiredInformation: ['The competing options', 'What "best" means here'],
    steps: [
      { step: 'S1', required: true, defaultMode: 'declared' },
      { step: 'S2', required: true, defaultMode: 'declared' },
      { step: 'S3', required: true, defaultMode: 'declared' },
      { step: 'S4', required: false, defaultMode: 'declared' },
      { step: 'S5', required: true, defaultMode: 'collaborative', recommendedToolIds: ['pairwise_ranking'] },
    ],
  },
];

export function getJob(jobId: string | undefined): SessionJob | undefined {
  return jobId ? SESSION_JOBS.find((j) => j.id === jobId) : undefined;
}
